import React from 'react';
import dhanKiDevi from '../../assets/dhan-ki-devi.webp';

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;

  if (/youtube\.com\/embed\//i.test(url)) {
    return url;
  }

  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) {
    return `https://www.youtube.com/embed/${watchMatch[1]}`;
  }

  const shortMatch = url.match(/youtu\.be\/([^?&/]+)/);
  if (shortMatch) {
    return `https://www.youtube.com/embed/${shortMatch[1]}`;
  }

  return null;
};

const isDirectImageUrl = (url) => (
  /\.(jpe?g|png|gif|webp|svg|avif)(\?|$)/i.test(url)
  || /airtableusercontent\.com/i.test(url)
);

/* Used only when Airtable is unreachable, keyed by the names in fallbackContent. */
const bundledFilmStills = {
  'dhan-ki-devi.webp': dhanKiDevi,
};

const getFilmMedia = (film) => {
  const youtubeEmbedUrl = getYouTubeEmbedUrl(film.youtubeUrl);
  if (youtubeEmbedUrl) {
    return { type: 'youtube', src: youtubeEmbedUrl };
  }

  const imageSrc = film.imageUrl
    || bundledFilmStills[film.imageName]
    || (isDirectImageUrl(film.youtubeUrl) ? film.youtubeUrl : null);
  if (imageSrc) {
    return { type: 'image', src: imageSrc };
  }

  if (film.youtubeUrl) {
    return { type: 'link', href: film.youtubeUrl, label: 'View link' };
  }

  return { type: 'none' };
};

export const PublicationItem = ({ publication }) => {
  if (publication.plainText) {
    return (
      <div className="pub-content">
        {publication.plainText}
      </div>
    );
  }

  const separator = publication.separator || ' in ';

  const titleElement = publication.url && !publication.plainTitle
    ? <a className="publication-link" href={publication.url}>{publication.title}</a>
    : publication.title;

  return (
    <div className="pub-content">
      {publication.prefix && (
        <>
          {publication.prefix}
          {' '}
        </>
      )}
      {titleElement}
      {publication.suffix && (
        <>
          {' '}
          {publication.suffix}
        </>
      )}
      {publication.venue && (
        <>
          {separator}
          <i>{publication.venue}</i>
        </>
      )}
    </div>
  );
};

export const EventItem = ({ event }) => {
  if (event.plainText) {
    return (
      <div className="pub-content">
        {event.plainText}
      </div>
    );
  }

  return (
    <div className="pub-content">
      {event.url ? (
        <a className="publication-link" href={event.url}>{event.title}</a>
      ) : (
        event.title
      )}
      {event.date && (
        <>
          ,
          {' '}
          {/* Dates typed in Airtable often start with a space of their own;
              the browser folds the two together into one. */}
          {event.date.trim()}
        </>
      )}
    </div>
  );
};

const hasFilmContent = (film) => Boolean(
  film.title || film.description || film.role
  || film.imageUrl || film.youtubeUrl || film.linkUrl,
);

export const FilmItem = ({ film }) => {
  // A half-filled row in Airtable would otherwise occupy a slot in the grid.
  if (!hasFilmContent(film)) {
    return null;
  }

  const media = getFilmMedia(film);

  return (
    <div className="film-card">
      {media.type === 'youtube' && (
        <div className="film-media">
          <iframe
            src={media.src}
            title={film.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      )}
      {media.type === 'image' && (
        <div className="film-media">
          <img
            className="film-image"
            src={media.src}
            alt={film.title || 'Film still'}
          />
        </div>
      )}
      {media.type === 'link' && (
        <div className="film-media film-media-link">
          <a className="publication-link" href={media.href} target="_blank" rel="noopener noreferrer">
            {media.label}
          </a>
        </div>
      )}
      <div className="film-text">
        {film.title && (
          <>
            {film.title}
            <br />
          </>
        )}
        {film.role && (
          <>
            Role:
            {' '}
            {/* Roles typed in Airtable often start with a space of their own;
                the browser folds the two together into one. */}
            {film.role.trim()}
            <br />
          </>
        )}
        {film.description}
        {film.linkUrl && (
          <>
            {' '}
            <a href={film.linkUrl}>{film.linkText || 'here'}</a>
            .
          </>
        )}
      </div>
    </div>
  );
};
